import { Opportunity } from '../types';

// SEO constants
export const SITE_CONFIG = {
  siteName: 'BC STEM Opportunities',
  siteUrl: 'https://bcstem.ca',
  description: 'Discover STEM opportunities across British Columbia. Find competitions, grants, scholarships, hackathons, internships, and workshops in one centralized platform.',
  keywords: 'STEM opportunities, British Columbia, BC, competitions, grants, scholarships, hackathons, science fair, technology, education, students, internships, workshops, research, innovation, UBC, SFU, BCIT',
  author: 'BC STEM Opportunities',
  creator: 'Nonit Gupta',
  twitterHandle: '@bcstemopp',
  locale: 'en_CA',
  themeColor: '#3B82F6',
};

// Generate dynamic page title
export const generatePageTitle = (pageTitle?: string): string => {
  if (!pageTitle) return SITE_CONFIG.siteName;
  return `${pageTitle} | ${SITE_CONFIG.siteName}`;
};

// Generate meta description for opportunity — mirrors
// scripts/generate-static.js opportunityMetaDescription(). Leads with content
// (the title is already in <title>); prepends the org only when it adds info.
export const generateOpportunityDescription = (opportunity: Opportunity): string => {
  const title = (opportunity.title || '').trim();
  const org = (opportunity.organization || '').trim();
  const desc = (opportunity.description || '').replace(/\s+/g, ' ').trim();
  const tl = title.toLowerCase();
  const ol = org.toLowerCase();
  const orgPart = org && !tl.includes(ol) && !ol.includes(tl) ? `${org}: ` : '';
  const body = desc || `A STEM opportunity${org ? ` from ${org}` : ''} in British Columbia.`;
  const full = `${orgPart}${body}`;
  const n = 155;
  if (full.length <= n) return full;
  let cut = full.slice(0, n - 1);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > n * 0.6) cut = cut.slice(0, lastSpace);
  return cut.trimEnd() + '…';
};

// Generate Open Graph data for opportunity
export const generateOpportunityOGData = (opportunity: Opportunity) => {
  return {
    title: `${opportunity.title} | BC STEM Opportunities`,
    description: generateOpportunityDescription(opportunity),
    url: `${SITE_CONFIG.siteUrl}/opportunity/${opportunity.id}`,
    type: 'article',
    image: `${SITE_CONFIG.siteUrl}/og-image.png`,
    'article:author': SITE_CONFIG.creator,
    'article:publisher': SITE_CONFIG.siteName,
    'article:section': 'Education',
    'article:tag': opportunity.tags.join(', '),
  };
};

// Schema.org type selection — kept in lockstep with scripts/generate-static.js.
// Only genuine, upcoming event types are Events; ongoing programs/camps/
// workshops are Courses; everything else (and past, non-recurring events) gets
// no rich-result type (breadcrumb only) rather than a misleading/past Event.
const EVENT_TYPES = ['Competition', 'Hackathon', 'Conference'];
const COURSE_TYPES = ['Workshop/Programs', 'Camp', 'Course', 'Program'];

const opportunityPrimaryType = (opportunity: Opportunity): string => {
  const t = opportunity.type as unknown;
  if (Array.isArray(t)) return String(t[0] || '').trim();
  return String(t || '').split(',')[0].trim();
};

const todayISODate = (): string => new Date().toISOString().slice(0, 10);

const dateOnlyStr = (iso?: string | null): string => {
  if (!iso) return '';
  const m = String(iso).match(/^\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : '';
};

const isPastDate = (iso: string | undefined | null, today: string): boolean => {
  const d = dateOnlyStr(iso);
  return !!d && d < today;
};

const parseOfferPrice = (
  cost?: string,
): { price: string; priceCurrency: string } | null => {
  const s = String(cost || '').trim();
  if (!s) return null;
  const numbers = s.match(/\d[\d,]*(?:\.\d+)?/g);
  if (!numbers) return /free|no cost/i.test(s) ? { price: '0', priceCurrency: 'CAD' } : null;
  if (numbers.length !== 1) return null;
  const currency = /\busd\b|US\$/i.test(s) ? 'USD' : 'CAD';
  return { price: numbers[0].replace(/,/g, ''), priceCurrency: currency };
};

const sanitizeLocationName = (loc?: string): string => {
  const s = String(loc || '').trim();
  if (!s || /^(in[- ]?person|virtual|online|hybrid|remote)$/i.test(s)) return 'British Columbia';
  return s;
};

const rollForwardDates = (
  startIso: string,
  endIso: string | undefined,
  today: string,
): { startDate: string; endDate?: string } => {
  const start = new Date(startIso);
  if (isNaN(start.getTime())) return { startDate: startIso, endDate: endIso };
  const todayD = new Date(`${today}T00:00:00.000Z`);
  let years = 0;
  while (start < todayD) {
    start.setUTCFullYear(start.getUTCFullYear() + 1);
    years += 1;
  }
  let rolledEnd = endIso;
  if (endIso) {
    const end = new Date(endIso);
    if (!isNaN(end.getTime())) {
      end.setUTCFullYear(end.getUTCFullYear() + years);
      rolledEnd = end.toISOString();
    }
  }
  return { startDate: start.toISOString(), endDate: rolledEnd };
};

// Generate structured data for an opportunity. Returns an Event, a Course, or
// null (breadcrumb-only) — mirrors scripts/generate-static.js exactly so the
// rendered DOM matches the build-time HTML.
export const generateOpportunityStructuredData = (opportunity: Opportunity): any | null => {
  const url = `${SITE_CONFIG.siteUrl}/opportunity/${opportunity.id}`;
  const image = `${SITE_CONFIG.siteUrl}/og-image.png`;
  const today = todayISODate();
  const type = opportunityPrimaryType(opportunity);
  const online = !!opportunity.interactionTypes?.includes('Virtual');
  const hybrid = !!opportunity.interactionTypes?.includes('Hybrid');
  const priced = parseOfferPrice(opportunity.cost);
  const offers = priced
    ? {
        '@type': 'Offer',
        price: priced.price,
        priceCurrency: priced.priceCurrency,
        availability: 'https://schema.org/InStock',
        url: opportunity.website || url,
      }
    : undefined;
  const educationalLevel =
    opportunity.gradeLevel && opportunity.gradeLevel.length
      ? opportunity.gradeLevel.join(', ')
      : undefined;
  const keywords =
    opportunity.tags && opportunity.tags.length ? opportunity.tags.join(', ') : undefined;
  const publisher = {
    '@type': 'Organization',
    name: SITE_CONFIG.siteName,
    url: SITE_CONFIG.siteUrl,
  };
  const isYearlyRecurring = !!(opportunity as { isYearlyRecurring?: boolean }).isYearlyRecurring;

  if (EVENT_TYPES.includes(type) && opportunity.startDate) {
    let start = opportunity.startDate;
    let end = opportunity.endDate || undefined;
    let emit = false;
    if (!isPastDate(opportunity.endDate || opportunity.startDate, today)) {
      emit = true;
    } else if (isYearlyRecurring) {
      const rolled = rollForwardDates(opportunity.startDate, opportunity.endDate || undefined, today);
      start = rolled.startDate;
      end = rolled.endDate;
      emit = true;
    }
    if (!emit) return null;
    const data: any = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: opportunity.title,
      description: opportunity.description || undefined,
      url,
      image,
      startDate: start,
      endDate: end,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: online
        ? 'https://schema.org/OnlineEventAttendanceMode'
        : hybrid
        ? 'https://schema.org/MixedEventAttendanceMode'
        : 'https://schema.org/OfflineEventAttendanceMode',
      location: online
        ? { '@type': 'VirtualLocation', url: opportunity.website || url }
        : {
            '@type': 'Place',
            name: sanitizeLocationName(opportunity.location),
            address: { '@type': 'PostalAddress', addressRegion: 'BC', addressCountry: 'CA' },
          },
      organizer: {
        '@type': 'Organization',
        name: opportunity.organization,
        url: opportunity.website || undefined,
      },
      keywords,
      publisher,
    };
    if (offers) data.offers = offers;
    if (educationalLevel) data.educationalLevel = educationalLevel;
    return data;
  }

  if (COURSE_TYPES.includes(type)) {
    const data: any = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: opportunity.title,
      description: opportunity.description || undefined,
      url,
      image,
      provider: {
        '@type': 'Organization',
        name: opportunity.organization,
        url: opportunity.website || undefined,
      },
      keywords,
      publisher,
    };
    if (offers) data.offers = offers;
    if (educationalLevel) data.educationalLevel = educationalLevel;
    return data;
  }

  return null;
};

// Generate canonical URL
export const generateCanonicalUrl = (path: string = ''): string => {
  return `${SITE_CONFIG.siteUrl}${path}`;
};

// Generate breadcrumb structured data
export const generateBreadcrumbData = (items: Array<{name: string, url: string}>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
};

// Generate FAQ structured data for about page
export const generateFAQStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What types of STEM opportunities are available?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We feature competitions, grants, scholarships, hackathons, internships, workshops, conferences, and other STEM-related opportunities across British Columbia.'
        }
      },
      {
        '@type': 'Question',
        name: 'Who can use BC STEM Opportunities?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our platform is designed for students, professionals, educators, and anyone interested in STEM opportunities in British Columbia, from elementary school through graduate level.'
        }
      },
      {
        '@type': 'Question',
        name: 'How often are opportunities updated?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Opportunities are updated regularly as new programs become available and as deadlines approach. We recommend checking back frequently for the latest opportunities.'
        }
      },
      {
        '@type': 'Question',
        name: 'Is the platform free to use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, BC STEM Opportunities is completely free to use. Our goal is to make STEM opportunities accessible to everyone in British Columbia.'
        }
      }
    ]
  };
};

// Web-specific SEO functions
export const updateDocumentTitle = (title: string) => {
  if (typeof document !== 'undefined') {
    document.title = title;
  }
};

export const updateMetaTag = (name: string, content: string, property?: boolean) => {
  if (typeof document !== 'undefined') {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector(selector) as HTMLMetaElement;
    
    if (!meta) {
      meta = document.createElement('meta');
      if (property) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      document.head.appendChild(meta);
    }
    
    meta.setAttribute('content', content);
  }
};

export const updateCanonicalLink = (url: string) => {
  if (typeof document !== 'undefined') {
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    
    link.setAttribute('href', url);
  }
};

export const updateStructuredData = (data: object, id: string = 'structured-data') => {
  if (typeof document !== 'undefined') {
    let script = document.querySelector(`script[data-id="${id}"]`);
    
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-id', id);
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(data);
  }
};

// Build-time seed written by scripts/generate-static.js on statically generated
// /opportunity/<id> pages. Only trusted when it names this same opportunity —
// on an in-session navigation (or the homepage modal) the seed belongs to a
// different page and must be ignored.
type OpportunitySeed = {
  id: string;
  canonicalId: string;
  related: Array<{ id: string; title: string; organization: string }>;
};

export const getOpportunitySeed = (
  opportunityId: string
): OpportunitySeed | null => {
  if (typeof window === 'undefined') return null;
  const seed = (window as any).__OPPORTUNITY_SEO__ as OpportunitySeed | undefined;
  return seed && seed.id === opportunityId ? seed : null;
};

// Update SEO for opportunity page
export const updateOpportunitySEO = (opportunity: Opportunity) => {
  // Drop the brand suffix on long titles (matches scripts/generate-static.js) so
  // the title stays within Google's ~60-char display limit.
  const title =
    opportunity.title.length > 45
      ? opportunity.title
      : generatePageTitle(opportunity.title);
  const description = generateOpportunityDescription(opportunity);
  const ogData = generateOpportunityOGData(opportunity);
  const structuredData = generateOpportunityStructuredData(opportunity);
  // This page's own URL — used for breadcrumb/JSON-LD, matching the static HTML.
  const pageUrl = generateCanonicalUrl(`/opportunity/${opportunity.id}`);
  // The canonical may point at a *different* record: byte-identical duplicates
  // are consolidated onto one survivor at build time. Rebuilding the canonical
  // from opportunity.id here silently undid that consolidation in the rendered
  // DOM — which is the copy Google indexes — so honour the build-time seed.
  const seed = getOpportunitySeed(opportunity.id);
  const canonicalUrl = seed
    ? generateCanonicalUrl(`/opportunity/${seed.canonicalId}`)
    : pageUrl;

  // Update document title
  updateDocumentTitle(title);
  
  // Update meta tags ( <meta name="keywords"> intentionally not set — Google
  // ignores it and the build-time HTML no longer emits it )
  updateMetaTag('description', description);

  // Update Open Graph tags
  Object.entries(ogData).forEach(([key, value]) => {
    updateMetaTag(key, value, true);
  });
  
  // Update Twitter Card tags
  updateMetaTag('twitter:title', ogData.title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', ogData.image);
  
  // Update canonical URL
  updateCanonicalLink(canonicalUrl);

  // Update structured data — Event, Course, or nothing, in lockstep with the
  // build-time generator. Remove any stale block when this opportunity has no
  // rich-result type so the rendered DOM never carries a mismatched one.
  if (structuredData) {
    updateStructuredData(structuredData, 'opportunity-structured-data');
  } else if (typeof document !== 'undefined') {
    document.querySelector('script[data-id="opportunity-structured-data"]')?.remove();
  }

  // Update breadcrumb data (2-item Home → opportunity, matching the static HTML;
  // the old 3-item form pointed at a non-canonical /opportunities node).
  const breadcrumbData = generateBreadcrumbData([
    { name: 'Home', url: `${SITE_CONFIG.siteUrl}/` },
    { name: opportunity.title, url: pageUrl }
  ]);
  updateStructuredData(breadcrumbData, 'breadcrumb-data');
};

// Update SEO for about page
export const updateAboutPageSEO = () => {
  const title = generatePageTitle('About');
  const description = 'Learn about BC STEM Opportunities, our mission to connect students and professionals with STEM opportunities across British Columbia, and how we\'re building a thriving STEM community.';
  
  updateDocumentTitle(title);
  updateMetaTag('description', description);
  updateCanonicalLink(generateCanonicalUrl('/about'));
  
  // Add FAQ structured data
  const faqData = generateFAQStructuredData();
  updateStructuredData(faqData, 'faq-structured-data');
}; 